import React from 'react';

import { Box, Button, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';

import styled from '@emotion/styled';
import { Formik } from 'formik';
import * as yup from 'yup';

import Icons from '../../../themes/icons';
import { formatNumberWithCommas } from '../../../utils/formatter';
import { CurrencySign } from '../../CurrencySign/CurrencySign';
import BaseDialog, { BaseDialogProps } from '../Base';

const schema = yup.object({
  balance: yup.number().min(1).required('Required'),
});

type Props = {
  title?: string;
  currency?: string;
  onSubmit: (totalAmountWithFee: number) => void;
  getOriginalPriceHandle?: (amount: number) => void;
} & BaseDialogProps;

const totalBalanceAfterMarketPlaceFee = (balance: number) => balance + balance * 0.05;

const AddBalanceDialog: React.FC<Props> = ({ onSubmit, getOriginalPriceHandle, ...rest }) => {
  const [localBalance, setLocalBalance] = React.useState(0);
  const [isConfirmed, setIsConfirmed] = React.useState(false);

  const handelIsConfirmChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setIsConfirmed(e.target.checked);
  }, []);

  return (
    <BaseDialog
      width='590px'
      height={localBalance > 0 ? '630px' : '590px'}
      titleComponent={<ModalTitle>+Add funds</ModalTitle>}
      {...rest}
    >
      <Box
        sx={{
          paddingX: '18px',
          paddingY: '12px',
        }}
      >
        <Heading>How much do you want to add to your Beautypass Balance?</Heading>
        <Formik
          validationSchema={schema}
          initialValues={{ balance: 0 }}
          onSubmit={(values) => {
            const totalAmount = values.balance + values.balance * 0.05;
            onSubmit(totalAmount);
            if (getOriginalPriceHandle) getOriginalPriceHandle(values.balance);
            if (rest?.onClose) rest?.onClose({}, 'backdropClick');
          }}
        >
          {({ values, handleSubmit, setValues }) => {
            const fee = values.balance * 0.05;
            return (
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  id='balance'
                  name='balance'
                  placeholder={'Amount (USD)'}
                  className='offer-input-field'
                  sx={{ marginTop: '24px' }}
                  type='text'
                  value={values.balance}
                  onChange={(event) => {
                    const _value = event.target.value.trim();
                    if (_value === '' || (/^[0-9]+$/.test(_value) && Number(_value) > 0)) {
                      setValues({ balance: _value === '' ? 0 : Number(_value) });
                      setLocalBalance(_value === '' ? 0 : Number(_value));
                    }
                  }}
                  InputProps={{
                    startAdornment: <Icons.DollarSignIcon />,
                  }}
                />
                <Box
                  sx={{
                    borderRadius: '8px',
                    backgroundColor: '#FAFAFA',
                    padding: '10px 15px',
                    mt: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                  }}
                >
                  <Icons.InfoLightIcon style={{ width: '24px', height: '24px' }} />
                  <Typography fontSize={'15px'} fontFamily={'Poppins'}>
                    <b>This deposit is non-refundable</b> and can only be used within the Beautypass
                    platform
                  </Typography>
                </Box>
                {values.balance > 0 && (
                  <>
                    <Box pr={3} my={2}>
                      <DiscountedText>
                        5% marketplace fee will be added to cover transaction costs. For a balance
                        addition of ${formatNumberWithCommas(values.balance)} the fee will be $
                        {formatNumberWithCommas(fee)}
                      </DiscountedText>
                    </Box>
                  </>
                )}
                <Box mt={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isConfirmed} // Check if selected
                        onChange={handelIsConfirmChanged}
                        sx={{ padding: '3px 9px', color: '#000' }}
                      />
                    }
                    label={
                      <Typography fontSize={'15px'} fontFamily={'Poppins'}>
                        I understand this <b>balance is non-refundable</b> and for platform use only
                      </Typography>
                    }
                    sx={{
                      alignItems: 'flex-start',
                      mt: localBalance > 4 ? 3 : 5,
                      mb: 0.5,
                    }}
                  />
                  <Button
                    fullWidth
                    variant='contained'
                    sx={{ paddingY: '15px', marginTop: '10px' }}
                    type='submit'
                    disabled={!isConfirmed || localBalance === 0}
                  >
                    Continue with
                    {values.balance && values.balance > 0 ? (
                      <Box display={'flex'} alignItems={'center'} gap={'4px'} marginLeft={'4px'}>
                        <span>
                          <CurrencySign currency={'usd'} />
                          <span>
                            {formatNumberWithCommas(
                              totalBalanceAfterMarketPlaceFee(values.balance),
                            )}
                          </span>
                        </span>
                      </Box>
                    ) : null}
                  </Button>
                </Box>
              </form>
            );
          }}
        </Formik>
      </Box>
    </BaseDialog>
  );
};

export default AddBalanceDialog;

const Heading = styled.h1`
  margin: 0;
  padding: 0;

  color: #111;
  font-size: 24px;
  font-weight: 500;
`;

const ModalTitle = styled.span`
  color: #848484;
  font-size: 15px;
  font-weight: 500;
`;

export const DiscountedText = styled.span`
  color: #848484;
  font-size: 15px;
  font-weight: 400;
  display: inline-block;
`;
